import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg, getParsedErrorResponse } from 'helpers/errorMsg'
import {
  getAATestMetricResults,
  getAATestMetrics,
  getNonLoginExperimentTypes,
  postAATest,
  postAATestResult,
  putStartExperimentLogin,
  putStartNonLoginExperiment,
  putStopExperimentLogin,
  putPauseExperiment,
  putResumeExperiment,
  putStopNonLoginExperiment,
} from 'features/ABAdmin/services/ABAdminAddEdit'
import {
  PostAATestPayloadType,
  PostAATestResultPayloadType,
  PutStartExperimentLoginRequestPayloadType,
} from 'features/ABAdmin/@types/ExperimentAddEdit'
import { AxiosError, AxiosResponse } from 'axios'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import { toastSuccess } from 'utils/toast'
import { SLICE_NAME_AB_ADMIN_ADD_EDIT, setAATestMetrics } from './slice'

/** Non-Login */
export const fetchNonLoginExperimentType = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_ADD_EDIT}/fetchNonLoginExperimentType`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getNonLoginExperimentTypes()

      return data?.data?.types || []
    } catch (err) {
      callErrorMsg(err, 'Gagal mendapatkan daftar tipe eksperimen')
      return rejectWithValue(err)
    }
  },
)

export const runExperimentNonLogin = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_ADD_EDIT}/runExperimentNonLogin`,
  async (experimentId: number, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await putStartNonLoginExperiment(experimentId)

      toastSuccess(data.message || 'Eksperimen berhasil dijalankan')

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const stopExperimentNonLogin = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_ADD_EDIT}/stopExperimentNonLogin`,
  async (experimentId: number, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await putStopNonLoginExperiment(experimentId)

      toastSuccess(data.message || 'Eksperimen berhasil dihentikan')

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

/** AA Test */
export const fetchAATestMetrics = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_ADD_EDIT}/fetchAATestMetrics`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getAATestMetrics()

      dispatch(setAATestMetrics(data.data.metrics))
    } catch (err) {
      callErrorMsg(err)
    }
  },
)

export const fetchAATestMetricResults = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_ADD_EDIT}/fetchAATestMetricResults`,
  async (experimentId: number) => {
    try {
      const { data } = await getAATestMetricResults(experimentId)
      return data
    } catch (err) {
      return Promise.reject(err)
    }
  },
)

export const startAATest = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_ADD_EDIT}/startAATest`,
  async (params: { experimentId: number; payload: PostAATestPayloadType }) => {
    const result = { success: false, message: '' }
    try {
      const { data } = await postAATest(params.experimentId, params.payload)
      if (data.data.success) {
        result.success = true
      }
    } catch (err) {
      callErrorMsg(err)
      if (err instanceof AxiosError) {
        const message = getParsedErrorResponse(err.response as AxiosResponse)
        result.message = message
      } else {
        result.message = SOMETHING_WHEN_WRONG
      }
    }

    return result
  },
)

export const selectAATestResult = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_ADD_EDIT}/selectAATestResult`,
  async (params: { experimentId: number; payload: PostAATestResultPayloadType }) => {
    try {
      const { data } = await postAATestResult(params.experimentId, params.payload)
      if (data.data.success) {
        return data.data
      }
      throw new Error(SOMETHING_WHEN_WRONG)
    } catch (err) {
      callErrorMsg(err)
      return Promise.reject(err)
    }
  },
)

/** Login */
export const runExperimentLogin = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_ADD_EDIT}/runExperimentLogin`,
  async (
    {
      experimentId,
      payload,
    }: {
      experimentId: number
      payload?: PutStartExperimentLoginRequestPayloadType
    },
    { rejectWithValue },
  ) => {
    try {
      const {
        data: { data },
      } = await putStartExperimentLogin(experimentId, payload || {})

      toastSuccess(data.message || 'Eksperimen berhasil dijalankan')

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const stopExperimentLogin = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_ADD_EDIT}/stopExperimentLogin`,
  async (experimentId: number, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await putStopExperimentLogin(experimentId)

      toastSuccess(data.message || 'Eksperimen berhasil dihentikan')

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const pauseExperiment = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_ADD_EDIT}/pauseExperiment`,
  async (experimentId: number, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await putPauseExperiment(experimentId)

      toastSuccess(data.message || 'Eksperimen berhasil dijeda')

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const resumeExperiment = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_ADD_EDIT}/resumeExperiment`,
  async (experimentId: number, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await putResumeExperiment(experimentId)

      toastSuccess(data.message || 'Eksperimen berhasil dijalankan kembali')

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)
